<template>
	<div class="flex w-full flex-col gap-2">
		<p
			v-if="showNextBillingDate"
			class="text-center text-sm font-semibold text-won-green"
		>
			Next Billing Date: {{ billingDate }}
		</p>
		<div
			v-if="showExpirationDate"
			class="flex h-9 w-full items-center justify-center rounded-md bg-won-red-light text-sm font-semibold text-won-red"
		>
			Expiration Date: {{ billingDate }}
		</div>
		<button
			v-if="showPaymentFailedWarning"
			type="button"
			:class="[
				'flex h-9 w-full items-center justify-center rounded-md bg-won-red-light text-sm font-semibold text-won-red',
				{ 'animate-payment-glow': shouldHighlightPaymentMethod },
			]"
			@click="handleUpdatePaymentMethod"
		>
			Update Payment Method
		</button>
		<button
			v-else-if="showNormalPaymentUpdate"
			type="button"
			:class="[
				'flex h-9 w-full items-center justify-center rounded-md bg-won-green text-sm font-medium text-white hover:bg-won-green/90',
				{ 'animate-payment-glow': shouldHighlightPaymentMethod },
			]"
			@click="handleUpdatePaymentMethod"
		>
			Update Payment Method
		</button>
		<button
			v-if="showCancelPlan"
			type="button"
			class="flex h-9 w-full items-center justify-center rounded-md bg-won-red-light text-sm font-semibold text-won-red"
			@click="handleDirectCancellation"
		>
			Cancel Plan
		</button>
		<Button
			v-if="showResume"
			class="w-full"
			@click="handleResume"
		>
			<LoadingIcon v-if="isSubmittingResume" />
			Resume
		</Button>
		<cancellation-flow-modal
			v-if="cancellationV2Enabled"
			:show-modal="showCancelSubscriptionModal"
			:close-modal="closeModalCancelSubscription"
			:cancel-subscription="cancelSubscription"
			:accept-retention-offer="handleAcceptRetentionOffer"
			:is-offer-eligible="isOfferEligible"
			:can-configure-criteria="canConfigureCriteria"
			:is-reactivating="isReactivating"
			@reactivate="handleReactivateSubscription"
		/>
		<cancel-subscription-modal
			v-else
			:show-modal="showCancelSubscriptionModal"
			:close-modal="closeModalCancelSubscription"
			:current-plan="userPlan?.stripe_price"
			@cancel-subscription:proceed="cancelSubscription"
		/>
		<ShowSuccessCancelSubscriptionModal
			:show-modal="showSuccessCancelSubscriptionModal"
			:close-modal="closeModalSuccessCancelSubscription"
			:active-until="activeUntil"
			:is-reactivating="isReactivating"
			@reactivate-subscription="handleReactivateSubscription"
		/>
	</div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import moment from 'moment';
import { useRoute, useRouter } from 'vue-router';
import { useSubscriptionPlanStore } from '../../../store/subscription.store';
import { useNotifications } from '../../../store/notifications.store';
import { useFeatureUsageStore } from '../../../store/feature-usage.store';
import { setAuthState } from '../../../composables/auth';
import { isSubscriptionCancellationV2Enabled } from '../../../composables/subscription-cancellation-v2';

import LoadingIcon from '../../../icons/loading-spinner.vue';
import Button from '../../library/button.vue';
import ShowSuccessCancelSubscriptionModal from './success-cancel-subscription-modal.vue';
import CancelSubscriptionModal from './cancel-subscription-modal.vue';
import CancellationFlowModal from './cancellation/cancellation-flow-modal.vue';

import { acceptRetentionOffer, cancel, resumeSubscription } from '../../../api/subscription';
import axios from '../../../utils/axios';

/**
 * The billing affordances of a user's own paid plan: next billing or expiration date, the
 * Stripe portal, cancellation (v1 modal or the v2 flow) and resuming during the grace period.
 */
const subscriptionStore = useSubscriptionPlanStore();
const route = useRoute();
const router = useRouter();
const featureUsageStore = useFeatureUsageStore();
const { getUser, setUser, setUserData, userHaveAccessTo } = setAuthState();
// Read fields directly off the store (it's reactive) instead of storeToRefs() on the
// whole store: subscription.store.js exposes a plain (non-ref) `stripeKey` that is
// `undefined` when VITE_STRIPE_KEY is unset (e.g. in CI), which makes storeToRefs() throw.
const userPlan = computed(() => subscriptionStore.userPlan);
const hasSubscription = computed(() => subscriptionStore.hasSubscription);
const isOnGracePeriod = computed(() => subscriptionStore.isOnGracePeriod);
const isOrgProvidedPremium = computed(() => subscriptionStore.isOrgProvidedPremium);
const user = ref(getUser());

const submitting = ref(false);
const isReactivating = ref(false);
const isSubmittingResume = ref(false);
const activeUntil = ref(null);
const showSuccessCancelSubscriptionModal = ref(false);
const showCancelSubscriptionModal = ref(false);
const cancellationV2Enabled = computed(() => isSubscriptionCancellationV2Enabled());
const isOfferEligible = computed(() => user.value?.retention_offer_eligible ?? false);
// Gates the cancellation flow's Recommendation Settings bridge on the same flag the drawer
// itself renders behind (jobboard-v3/pages/search-jobs.vue).
const canConfigureCriteria = computed(() => userHaveAccessTo('criteria-weighted-search'));
const billingDate = ref(user.value?.next_billing_date);

/**
 * `premium_billing_cycle` is null unless the user pays for their own Premium subscription, so
 * comped (`'price_premium'`) and organization-provided (EN-928) plans never get billing
 * affordances. A user cached before the field existed falls back to the old rule: a real
 * Stripe price rather than the comped placeholder.
 */
const ownsBillablePlan = computed(() => {
	if (isOrgProvidedPremium.value || !hasSubscription.value) {
		return false;
	}

	if (user.value?.premium_billing_cycle !== undefined) {
		return !!user.value.premium_billing_cycle;
	}

	return !!userPlan.value?.stripe_price && userPlan.value.stripe_price !== 'price_premium';
});

const showNextBillingDate = computed(() =>
	ownsBillablePlan.value && !isOnGracePeriod.value && !user.value?.payment_failed_at
);
const showExpirationDate = computed(() => ownsBillablePlan.value && isOnGracePeriod.value);
const showPaymentFailedWarning = computed(() => ownsBillablePlan.value && !!user.value?.payment_failed_at);
const showNormalPaymentUpdate = computed(() => ownsBillablePlan.value && !user.value?.payment_failed_at);
const showCancelPlan = computed(() =>
	ownsBillablePlan.value && !isOnGracePeriod.value && !user.value?.payment_failed_at
);
const showResume = computed(() => ownsBillablePlan.value && isOnGracePeriod.value);

const shouldHighlightPaymentMethod = computed(() =>
	route.query?.tab === 'billing' && route.query?.highlight === 'payment'
);

const handleUpdatePaymentMethod = async () => {
	const notificationStore = useNotifications();
	try {
		const response = await axios.post('/api/subscriptions/create-portal-session');

		if (response.data.url) {
			window.location.href = response.data.url;
		} else {
			notificationStore.notify('Failed to open payment portal. Please try again.', 'error');
		}
	} catch (error) {
		const errorMessage = error.response?.data?.error || error.message || 'Failed to open payment portal';
		notificationStore.notify(errorMessage, 'error');
	}
};

const handleDirectCancellation = () => {
	showCancelSubscriptionModal.value = true;
};

const closeModalSuccessCancelSubscription = () => {
	showSuccessCancelSubscriptionModal.value = false;
};
const closeModalCancelSubscription = () => {
	showCancelSubscriptionModal.value = false;
};

const cancelSubscription = async (payload) => {
	const notificationStore = useNotifications();
	submitting.value = true;

	try {
		const response = await cancel(payload);

		if (response?.success) {
			submitting.value = false;
			setUser(response?.user);
			subscriptionStore.setUserPlan(response?.user?.current_plan, response?.user?.on_grace_period);
			featureUsageStore.setFeatureUsage(response?.user?.feature_usage);
			setUserData(response?.user);

			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'subscription_cancelled',
			});

			activeUntil.value = moment(response?.user?.current_plan?.ends_at).format('MM-DD-YYYY');

			// The v2 flow shows its own confirmation in place, so the modal has to stay
			// open — closing it would send the user back to the reason step.
			if (!cancellationV2Enabled.value) {
				showCancelSubscriptionModal.value = false;
				showSuccessCancelSubscriptionModal.value = true;
			}
		}

		if (response?.error) {
			submitting.value = false;
			notificationStore.notify(response?.error ?? 'Something went wrong', 'error');
		}

		return response;
	} catch (error) {
		const message = error?.response?.data?.message || error?.message || 'Something went wrong';
		notificationStore.notify(message, 'error');
		submitting.value = false;
	}
};

/**
 * Apply the cancellation flow's 30%-off retention offer (EN-880). Unlike `cancel()`, the
 * endpoint reports failures with real status codes, so this catches instead of reading an
 * `error` off a 200 body.
 */
const handleAcceptRetentionOffer = async () => {
	const notificationStore = useNotifications();

	try {
		const response = await acceptRetentionOffer();

		setUserData(response?.user);
		subscriptionStore.setUserPlan(response?.user?.current_plan, response?.user?.on_grace_period);
		featureUsageStore.setFeatureUsage(response?.user?.feature_usage);
		// `user` was read once at setup, so refresh it: reopening the flow must see the
		// now-spent eligibility rather than offering a discount the endpoint would reject.
		user.value = getUser();

		return response;
	} catch (error) {
		const message = error?.response?.data?.error || error?.message || 'Something went wrong';
		notificationStore.notify(message, 'error');
	}
};

/**
 * Resume sends the user's own price, not the catalog's: the backend matches it with
 * `subscribedToPrice()`, and a God Mode subscriber's price is not the monthly default.
 */
const handleResume = async () => {
	const notificationStore = useNotifications();
	isSubmittingResume.value = true;

	try {
		const result = await resumeSubscription(userPlan.value?.stripe_price);

		if (result?.user != null) {
			setUser(result.user);
			subscriptionStore.setUserPlan(result.user.current_plan, result.user.on_grace_period);
			notificationStore.notify('Successfully resumed your subscription');
		}
	} finally {
		isSubmittingResume.value = false;
	}
};

const handleReactivateSubscription = async () => {
	isReactivating.value = true;
	try {
		await handleResume();
	} finally {
		isReactivating.value = false;
		closeModalSuccessCancelSubscription();
		closeModalCancelSubscription();
	}
};

watch(
	() => route.query?.highlight,
	(highlight) => {
		if (highlight === 'payment') {
			const timeout = setTimeout(() => {
				const rest = { ...route.query };
				delete rest.highlight;
				router.replace({ path: route.path, query: rest });
			}, 5000);
			return () => clearTimeout(timeout);
		}
	},
	{ immediate: true }
);
</script>
